"use client";

import { X } from "lucide-react";
import { useEffect, useRef } from "react";
import { NOTIF_BANNER_MS } from "@/lib/notificationBanner";
import { notifIcon } from "@/lib/notificationPrefs";

export type NotifBannerData = {
  id: string;
  type: string;
  title: string;
  body: string;
  /** Tujuan ketukan (`getNotifRoute`) — halaman yang sama dengan daftar Notifikasi. */
  href: string;
};

/** Geser ke atas sejauh ini (px) = tutup. */
const SWIPE_CLOSE_PX = 24;

/**
 * Banner notifikasi MASUK di atas layar (lihat `shouldShowNotifBanner`).
 * Menghilang sendiri setelah `NOTIF_BANNER_MS`, bisa ditutup (✕ / geser ke
 * atas), dan diketuk untuk membuka tujuannya — notifnya sekaligus ditandai
 * dibaca oleh pemanggil. Warna dasar putih + aksen token tema, di atas header
 * (z-30) & navigasi bawah (z-40), di bawah popup/dialog.
 */
export function NotificationBanner({
  banner,
  onOpen,
  onClose,
}: {
  banner: NotifBannerData;
  onOpen: (banner: NotifBannerData) => void;
  onClose: () => void;
}) {
  const startY = useRef<number | null>(null);

  // Timer dimulai ulang untuk tiap banner baru (id berganti).
  useEffect(() => {
    const timer = window.setTimeout(onClose, NOTIF_BANNER_MS);
    return () => window.clearTimeout(timer);
  }, [banner.id, onClose]);

  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-50 mx-auto w-full max-w-md px-3 pt-2 safe-top">
      <div
        key={banner.id}
        role="status"
        aria-live="polite"
        onTouchStart={(event) => {
          startY.current = event.touches[0]?.clientY ?? null;
        }}
        onTouchMove={(event) => {
          const y = event.touches[0]?.clientY;
          if (startY.current !== null && y !== undefined && y - startY.current < -SWIPE_CLOSE_PX) {
            startY.current = null;
            onClose();
          }
        }}
        className="pointer-events-auto flex animate-notif-drop items-start gap-2 rounded-2xl border-l-4 border-shopee bg-white p-3 shadow-[0_10px_30px_rgba(34,33,38,0.18)] motion-reduce:animate-none"
      >
        <button
          type="button"
          onClick={() => onOpen(banner)}
          className="flex min-w-0 flex-1 items-start gap-2.5 text-left"
        >
          <span aria-hidden className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-shopeeSoft text-lg">
            {notifIcon(banner.type)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-extrabold text-ink">{banner.title}</span>
            <span className="mt-0.5 line-clamp-2 text-xs font-medium text-muted">{banner.body}</span>
          </span>
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Tutup notifikasi"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-muted"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
